import { Component, type ReactNode } from "react";

/**
 * React error boundary — catches any crash in the visualization and
 * auto-reloads the page after 5 seconds. Better than a white screen
 * on an unattended kiosk display.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  private reloadTimer?: number;

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[error-boundary]", error, info);
    this.reloadTimer = window.setTimeout(() => {
      console.warn("[error-boundary] auto-reloading after crash");
      window.location.reload();
    }, 5000);
  }

  componentWillUnmount() {
    if (this.reloadTimer) window.clearTimeout(this.reloadTimer);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "JetBrains Mono, monospace",
            fontSize: 14,
            color: "#5c6b7e",
            background: "#05070a",
            letterSpacing: "0.2em",
          }}
        >
          RECOVERING...
        </div>
      );
    }
    return this.props.children;
  }
}
